import React, { useEffect, useState } from "react";
import { Row, Col, Modal, Button, message } from "antd";
import { DownOutlined, EditOutlined, UpOutlined } from "@ant-design/icons";
import DeleteIcon from "../../../Assets/delete.svg";
import API_MANAGER from "../../../API";
import {
  formatExperienceDateRange,
  getExperienceId,
  getGroupTenure,
  sortExperiencesLatestFirst,
} from "./experienceHelpers";

function JobPositionCard({
  data,
  onUpdate,
  userId,
  onEdit,
  showDelete = true,
}) {
  const [experience, setExperience] = useState(data);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSteps, setOpenSteps] = useState({});

  useEffect(() => {
    if (data) {
      setExperience(data);
    }
  }, [data]);

  const parentId = getExperienceId(experience);
  const related = (experience?.relatedExperiences || []).filter(
    (item) => getExperienceId(item) !== parentId
  );
  const steps = sortExperiencesLatestFirst([experience, ...related]);
  const employerLetter = (experience?.employerName || "E").charAt(0).toUpperCase();
  const groupTenure = getGroupTenure(experience);

  const toggleStep = (stepId) => {
    setOpenSteps((prev) => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  return (
    <>
      <div className="jobPositionCard experienceListCard">
        <div className="experienceEmployerRow">
          <div className="experienceEmployerMain">
            <div className="experienceEmployerAvatar">{employerLetter}</div>
            <div>
              <p className="experienceEmployerName">
                {experience?.employerName}
              </p>
              {groupTenure ? (
                <p className="experienceEmployerTenure">{groupTenure}</p>
              ) : null}
            </div>
          </div>
          {onEdit ? (
            <div className="experienceCardActions">
              {showDelete ? (
                <img
                  src={DeleteIcon}
                  alt="delete"
                  onClick={() => setDeleteModalVisible(true)}
                />
              ) : null}
              <EditOutlined
                className="experienceEditIcon"
                onClick={() => onEdit?.(experience)}
              />
            </div>
          ) : null}
        </div>

        <div className="experienceTimeline">
          {steps.map((step, index) => {
            const stepId = getExperienceId(step) || index;
            const skills = step?.skills || [];
            const firstSkill = skills[0];
            const extraSkills = skills.slice(1);
            const showMore = !!openSteps[stepId];

            return (
              <div
                className={`experienceTimelineStep ${
                  index === steps.length - 1 ? "last" : ""
                }`}
                key={stepId}
              >
                <div className="experienceTimelineRail">
                  <span className="experienceTimelineDot" />
                  {index !== steps.length - 1 && (
                    <span className="experienceTimelineLine" />
                  )}
                </div>
                <div className="experienceTimelineContent">
                  <div className="experienceStepTitleRow">
                    <p className="experienceStepTitle">
                      {step?.jobTitle?.name || "Role"}
                    </p>
                    {step?.currentRole && (
                      <div className="currentRole">Current</div>
                    )}
                  </div>
                  {step?.projectName ? (
                    <p className="experienceStepMeta">{step.projectName}</p>
                  ) : null}
                  {step?.industry?.name ? (
                    <p className="experienceStepMeta">{step.industry.name}</p>
                  ) : null}
                  <p className="experienceStepDates">
                    {formatExperienceDateRange(step)}
                  </p>
                  {firstSkill?.name ? (
                    <p className="experienceStepSkill">
                      <span className="experienceSkillDiamond">◇</span>
                      {firstSkill.name}
                    </p>
                  ) : null}
                  {showMore && (
                    <div className="experienceStepExtra">
                      {extraSkills.map((skill) => (
                        <p className="experienceStepSkill" key={skill.id || skill._id || skill.name}>
                          <span className="experienceSkillDiamond">◇</span>
                          {skill.name}
                        </p>
                      ))}
                      {step?.discipline?.name ? (
                        <p className="experienceStepMeta">
                          Discipline: {step.discipline.name}
                        </p>
                      ) : null}
                      {(step?.inductions || []).length > 0 ? (
                        <p className="experienceStepMeta">
                          Inductions:{" "}
                          {step.inductions.map((item) => item.name).join(", ")}
                        </p>
                      ) : null}
                      {step?.hasReferee ? (
                        <p className="experienceStepMeta">
                          Referee: {step.refereeName}
                          {step.refereePosition ? ` · ${step.refereePosition}` : ""}
                          {step.refereeContact ? ` · ${step.refereeContact}` : ""}
                        </p>
                      ) : null}
                    </div>
                  )}
                  <button
                    type="button"
                    className="experienceShowMore"
                    onClick={() => toggleStep(stepId)}
                  >
                    {showMore ? "Show less" : "Show more"}{" "}
                    {showMore ? <UpOutlined /> : <DownOutlined />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Modal
        className="candidateDeleteModal"
        centered
        onCancel={() => {
          setDeleteModalVisible(false);
        }}
        open={deleteModalVisible}
        footer={null}
        title="Delete Experience?"
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Are you sure you want to delete this experience?
            </p>
          </Col>
        </Row>

        <Row align={"middle"} gutter={16} justify={"end"}>
          <Col>
            <Button
              className="confirmBtn"
              onClick={async () => {
                try {
                  setLoading(true);
                  await API_MANAGER.deleteCandidateExperience(
                    data._id || data.id,
                    userId
                  );
                  message.success("Experience deleted successfully");
                  setDeleteModalVisible(false);
                  if (onUpdate) {
                    await onUpdate();
                  }
                } catch (error) {
                  message.error(
                    error?.response?.data?.message?.message ||
                      "Failed to delete experience"
                  );
                } finally {
                  setLoading(false);
                }
              }}
              loading={loading}
            >
              Delete
            </Button>
          </Col>
          <Col>
            <Button
              className="cancelBtn"
              onClick={() => {
                setDeleteModalVisible(false);
              }}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
}

export default JobPositionCard;
